import { cn } from "../utils/cn";

// ── Types ─────────────────────────────────────────────────────────────────────

export type InclusionType = "included" | "missing" | "neutral";

export interface InclusionChipProps {
  label: string;
  type: InclusionType;
  size?: "sm" | "md";
  className?: string;
}

// ── Config ────────────────────────────────────────────────────────────────────

const TYPE_CONFIG: Record<
  InclusionType,
  { bg: string; text: string; border: string; prefix: string }
> = {
  included: {
    bg:     "bg-green-50 dark:bg-green-900/20",
    text:   "text-green-700 dark:text-green-400",
    border: "border-green-200 dark:border-green-800/50",
    prefix: "✓",
  },
  missing: {
    bg:     "bg-red-50 dark:bg-red-900/20",
    text:   "text-red-700 dark:text-red-400",
    border: "border-red-200 dark:border-red-800/50",
    prefix: "✗",
  },
  neutral: {
    bg:     "bg-surface-overlay dark:bg-surface-overlay",
    text:   "text-content-secondary",
    border: "border-border-default dark:border-border-strong",
    prefix: "",
  },
};

const SIZE_CLASS = {
  sm: "px-2 py-0.5 text-[11px] gap-1 rounded-md",
  md: "px-2.5 py-1 text-xs gap-1.5 rounded-lg",
};

// ── Component ─────────────────────────────────────────────────────────────────

export function InclusionChip({
  label,
  type,
  size = "sm",
  className,
}: InclusionChipProps) {
  const { bg, text, border, prefix } = TYPE_CONFIG[type];

  return (
    <span
      className={cn(
        "inline-flex items-center font-medium border whitespace-nowrap",
        bg, text, border, SIZE_CLASS[size],
        className,
      )}
    >
      {prefix && (
        <span aria-hidden="true" className="font-bold leading-none">
          {prefix}
        </span>
      )}
      {label}
    </span>
  );
}

InclusionChip.displayName = "InclusionChip";
